import React, { useMemo, useState } from 'react';
import { Button } from '../components/ui/button';
import { Copy, Check } from 'lucide-react';

/**
 * ShareHelper — one-purpose page that takes a pre-built social snippet from
 * the URL (either `?text=<base64>` or `#<base64>`) and surfaces one giant
 * "Copy" button. Used by the admin product-alert email so Jake can one-tap
 * grab an Instagram / Threads caption on his phone, then paste into the
 * respective composer.
 *
 * Reading from `#<hash>` is preferred because the fragment never gets sent
 * to the server or captured in referrer headers.
 */
export default function ShareHelper() {
  const [copied, setCopied] = useState(false);

  const text = useMemo(() => {
    try {
      // Priority: hash first, then query string
      const rawHash = (window.location.hash || '').replace(/^#/, '');
      const params = new URLSearchParams(window.location.search);
      const rawQuery = params.get('text') || '';
      const raw = rawHash || rawQuery;
      if (!raw) return '';
      // Base64 decode (URL-safe) — the email encodes with base64.urlsafe_b64encode
      const normalized = raw.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
      return decodeURIComponent(escape(atob(padded)));
    } catch (e) {
      return '';
    }
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Fallback for older browsers / insecure contexts
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (_) {}
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-5 py-10" data-testid="share-helper-page">
      <div className="w-full max-w-lg">
        <h1 className="text-2xl md:text-3xl font-black text-white mb-2 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Copy your post
        </h1>
        <p className="text-sm text-neutral-400 mb-6">
          One tap → copy the snippet → switch to Instagram or Threads → paste.
        </p>

        <div
          className="bg-neutral-900 border border-white/10 rounded-2xl p-5 text-[15px] leading-relaxed text-neutral-100 whitespace-pre-wrap break-words mb-5"
          data-testid="share-helper-snippet"
        >
          {text || <span className="text-neutral-500 italic">No snippet provided.</span>}
        </div>

        <Button
          onClick={handleCopy}
          disabled={!text}
          className={`w-full h-14 text-base font-bold rounded-2xl transition-all ${
            copied
              ? 'bg-[#00FF94] text-black hover:bg-[#00FF94]'
              : 'bg-[#00F0FF] text-black hover:bg-[#00D4E0]'
          }`}
          data-testid="share-helper-copy-btn"
        >
          {copied ? (
            <>
              <Check className="w-5 h-5 mr-2" /> Copied — now paste it in the app
            </>
          ) : (
            <>
              <Copy className="w-5 h-5 mr-2" /> Copy to clipboard
            </>
          )}
        </Button>

        <p className="text-xs text-neutral-600 text-center mt-6">
          theailedger.ai · share helper
        </p>
      </div>
    </div>
  );
}
